import { useEffect, useState, useCallback } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/contexts/ToastContext';
import {
  getPushStatus,
  enableNotifications,
  disableNotifications,
  type PushStatus,
} from '@/lib/push-client';
import { friendlyPushError } from '@/lib/push-error';

const isIOSDevice = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isSafari = typeof navigator !== 'undefined'
  && /Safari/i.test(navigator.userAgent)
  && !/Chrome|CriOS|Android/i.test(navigator.userAgent);

export default function NotificationsSettings() {
  const { addToast } = useToast();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [action, setAction] = useState<'none' | 'enabling' | 'disabling'>('none');
  const [localError, setLocalError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLocalError(null);
    try {
      setStatus(await getPushStatus());
    } catch (err: any) {
      setStatus(null);
      setLocalError(err?.message || 'Could not load notification settings.');
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const onEnable = async () => {
    setAction('enabling');
    const result = await enableNotifications();
    setAction('none');
    await loadStatus();
    if (result.ok) {
      addToast('Notifications enabled', 'success');
    } else {
      const msg = friendlyPushError(result);
      setLocalError(msg);
      addToast(msg, 'error');
    }
  };

  const onDisable = async () => {
    setAction('disabling');
    const result = await disableNotifications();
    setAction('none');
    await loadStatus();
    if (result.ok) {
      addToast('Notifications disabled', 'success');
    } else {
      const msg = friendlyPushError({ ok: false, code: 'unknown', error: result.error });
      setLocalError(msg);
      addToast(msg, 'error');
    }
  };

  if (!status || status.supported === false) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Notifications</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-secondary">
            Receive alerts for labour approvals and submissions on your device.
          </p>
          {!status ? (
            <p className="text-sm text-muted mt-3">Loading notification settings...</p>
          ) : (
            <p className="text-sm text-muted mt-3">
              Web push is not supported in this browser. Use a supported Android, iPhone
              (added to Home Screen), or desktop browser to receive alerts.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const needsIosHomeScreen = isIOSDevice && !status.standalone;

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="text-base">Notifications</CardTitle>
        {status.enabled ? (
          <Badge variant="success">Notifications ✓ On</Badge>
        ) : (
          <Badge variant="default">Notifications ● Off</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-secondary">
          Receive alerts for labour approvals and submissions on this device.
        </p>

        {needsIosHomeScreen && (
          <p className="text-sm text-warning bg-warning/10 border border-warning/20 rounded-lg p-3">
            To receive push notifications on iPhone/iPad, add this app to your Home Screen
            (use <span className="font-medium">Share &gt; Add to Home Screen</span>), then
            enable notifications from the installed app.
          </p>
        )}

        {localError && (
          <p className="text-sm text-error">{localError}</p>
        )}

        <div className="flex items-center gap-3">
          {status.enabled ? (
            <Button
              variant="outline"
              onClick={onDisable}
              loading={action === 'disabling'}
              disabled={action === 'enabling'}
            >
              <BellOff size={16} />
              Disable Notifications
            </Button>
          ) : (
            <Button onClick={onEnable} loading={action === 'enabling'} disabled={action === 'disabling'}>
              <Bell size={16} />
              Enable Notifications
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
